import React, { useState, memo, useContext } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { detect } from '../../utils/detect-browser';
import SliderUnit from './SliderUnit';
import {
    CurrentAnswer,
    CircleWrapper,
    AnswerGroup,
    Answer,
    calcX,
    calcY
} from './Components';

import {
    onInteractWithSlider,
    onRadialSliderAnswered
} from '../../containers/RadialSlider/actions';

import { CardTypes } from '../../utils/enums';
import { getActionCompletedTime } from '../../utils/actionTiming';
import { isPortrait } from '../../utils/size';
import ThemeContext from '../../utils/theming';
import { formatThousands, Placeholders } from '../../utils/fillDynamicText';
import { roundHalf } from '../../utils/radialSlider/helpers';

const Slider = props => {
    const theme = useContext(ThemeContext);
    const [userAnswer, setUserAnswer] = useState(props.sliderValues.min);
    const [displayValue, SetDisplayValue] = useState(null);
    const counterRef = React.createRef();
    const {
        actionCompletedTime,
        actionIdentifier,
        actionInitiated,
        answerId,
        answers,
        answerTemplate,
        apiRequestData,
        correctAnswerAngle,
        correctValue,
        customColor,
        customLogo,
        d,
        hasLongAnswers,
        hasNextAction,
        isAnswered,
        isMultiActionsCard,
        isPoll,
        isSubmitting,
        onAnswered,
        onInteractWithSlider,
        onRadialSliderAnswered,
        prevAnswer,
        result,
        sliderValues,
        stepSize,
        isBYOBDialPreviewNumbers
    } = props;
    const strokeWidth = 5;
    const radius = d / 2;

    function formatDisplayValueTemplate(value) {
        const formattedValue =
            displayValue !== null
                ? Math.round(displayValue)
                : stepSize === 0.5
                ? roundHalf(value).toFixed(1)
                : formatThousands(Math.round(value));
        return answerTemplate
            .replace(
                new RegExp(Placeholders.RadialCorrectAnswerPlaceholder, 'g'),
                formattedValue
            )
            .replace(
                new RegExp(Placeholders.RadialUserAnswerPlaceholder, 'g'),
                formattedValue
            );
    }

    function setVisibleNumber(n) {
        if (counterRef.current) {
            counterRef.current.innerHTML = formatDisplayValueTemplate(n);
        }
    }

    function countFinished(interval) {
        if (counterRef.current) {
            counterRef.current.innerHTML = formatDisplayValueTemplate(correctValue);
            SetDisplayValue(correctValue);
        }
        clearInterval(interval);
    }

    function numberTransition() {
        if (displayValue !== null) return;
        const isReverse = correctValue < userAnswer;
        const totalDistance = Math.abs(sliderValues.max - sliderValues.min);
        const travelDistance = Math.abs(userAnswer - correctValue);
        const step = (sliderValues.max / 100) * (travelDistance / totalDistance);
        const intervalTime = 1200 / (travelDistance / step);
        let calculatedValue = userAnswer;
        const transitionDelayTimeout = setTimeout(() => {
            const transitionInterval = setInterval(() => {
                if (isReverse) {
                    if (calculatedValue > correctValue) {
                        calculatedValue -= step;
                        setVisibleNumber(calculatedValue);
                    } else {
                        countFinished(transitionInterval);
                    }
                } else {
                    if (calculatedValue < correctValue) {
                        calculatedValue += step;
                        setVisibleNumber(calculatedValue);
                    } else {
                        countFinished(transitionInterval);
                    }
                }
            }, intervalTime);
            clearTimeout(transitionDelayTimeout);
        }, 250);
    }

    return (
        <CircleWrapper
            isPortrait={isPortrait()}
            hasNextAction={hasNextAction}
            isAnswered={isAnswered}
        >
            <SliderUnit
                theme={theme}
                stepSize={stepSize}
                allowClick={false}
                allowReanswer={isPoll}
                animationDuration={1200}
                arcSize={270}
                correctAnswerAngle={correctAnswerAngle}
                correctValue={correctValue}
                customColor={customColor}
                customLogo={customLogo}
                isAnswered={isAnswered}
                isPoll={isPoll}
                min={sliderValues.min}
                max={sliderValues.max}
                radius={d / 2}
                result={result}
                rotationOffset={-45}
                strokeWidth={strokeWidth}
                thumbSize={30}
                value={userAnswer}
                isSubmitting={isSubmitting}
                onAfterChange={() => {
                    if (isSubmitting === false) {
                        onInteractWithSlider(actionInitiated, actionIdentifier);
                    }
                    if (typeof onAnswered === 'function') {
                        if (!isAnswered) onAnswered();
                    }
                    onRadialSliderAnswered(
                        apiRequestData,
                        actionIdentifier,
                        actionCompletedTime(),
                        userAnswer,
                        answerId,
                        isAnswered,
                        isMultiActionsCard,
                        prevAnswer !== userAnswer
                    );
                }}
                onChange={val => {
                    if (isSubmitting === false) {
                        setUserAnswer(val);
                        onInteractWithSlider(actionInitiated, actionIdentifier);
                    }
                }}
            />
            <AnswerGroup d={d}>
                {answers.map((a, i) => {
                    const distanceFromCenter = radius + (hasLongAnswers ? 30 : 25);
                    const sectionWidth = (270 / (answers.length - 1)) * i;

                    return (
                        <Answer
                            isBYOBDialPreviewNumbers={isBYOBDialPreviewNumbers}
                            key={(a || 'answer') + i}
                            answer={formatThousands(a)}
                            distanceFromCenter={distanceFromCenter}
                            style={{
                                transform:
                                    detect().name === 'ie' &&
                                    `translate(${calcX(
                                        0,
                                        distanceFromCenter,
                                        sectionWidth
                                    )}px, ${calcY(
                                        0,
                                        distanceFromCenter,
                                        sectionWidth
                                    )}px) rotate(135deg)`
                            }}
                            sectionWidth={sectionWidth}
                        />
                    );
                })}
            </AnswerGroup>
            <CurrentAnswer>
                <span ref={counterRef}>
                    {isAnswered && !isPoll
                        ? numberTransition()
                        : formatDisplayValueTemplate(
                              stepSize === 0.5
                                  ? roundHalf(userAnswer).toFixed(1)
                                  : userAnswer
                          )}
                </span>
            </CurrentAnswer>
        </CircleWrapper>
    );
};

function arePropsEqual(prevProps, nextProps) {
    if (prevProps.isAnswered !== nextProps.isAnswered || nextProps.isPoll === true) {
        return false;
    } else {
        return true;
    }
}

Slider.propTypes = {
    actionCompletedTime: PropTypes.func,
    actionIdentifier: PropTypes.string.isRequired,
    actionInitiated: PropTypes.bool,
    answerId: PropTypes.any,
    answers: PropTypes.array.isRequired,
    answerTemplate: PropTypes.string,
    apiRequestData: PropTypes.object.isRequired,
    correctAnswerAngle: PropTypes.number,
    correctValue: PropTypes.number,
    customColor: PropTypes.string.isRequired,
    customLogo: PropTypes.any,
    d: PropTypes.number.isRequired,
    hasNextAction: PropTypes.bool.isRequired,
    isAnswered: PropTypes.bool.isRequired,
    isBYOBDialPreviewNumbers: PropTypes.bool,
    isMultiActionsCard: PropTypes.bool.isRequired,
    prevAnswer: PropTypes.number,
    isPoll: PropTypes.bool.isRequired,
    hasLongAnswers: PropTypes.bool.isRequired,
    onAnswered: PropTypes.func.isRequired,
    onInteractWithSlider: PropTypes.func.isRequired,
    onRadialSliderAnswered: PropTypes.func.isRequired,
    result: PropTypes.number,
    isSubmitting: PropTypes.bool.isRequired,
    sliderValues: PropTypes.shape({
        min: PropTypes.number.isRequired,
        max: PropTypes.number.isRequired
    }),
    stepSize: PropTypes.number
};

export default connect(
    (state, props) => {
        const activeAction = state.widget.activeAction;
        const isAnswered = props.isAnswered;

        const correctValueToAngle = (correctValue, min, max) => {
            const answerLessMin = correctValue - min;
            const range = max - min;
            return (answerLessMin / range) * 270;
        };
        const correctValue = isAnswered ? state.radialSlider.correctValue : null;

        return {
            hasLongAnswers: props.answers.some(a => (a || '').toString().length >= 5),
            actionCompletedTime: getActionCompletedTime(state, 'radialSlider'),
            actionIdentifier: activeAction.identifier,
            actionInitiated: state.radialSlider.actionInitiated,
            activeAction,
            answerId: isAnswered ? state.radialSlider.answerId : null,
            answerTemplate: activeAction.feedbackAnswer || '',
            apiRequestData: state.widget.apiRequestData,
            correctValue,
            correctAnswerAngle: isAnswered
                ? correctValueToAngle(
                      correctValue,
                      props.sliderValues.min,
                      props.sliderValues.max
                  )
                : null,
            isAnswered,
            isBYOBDialPreviewNumbers: activeAction.isBYOBDialPreviewNumbers,
            isSubmitting: state.radialSlider.isSubmitting,
            isMultiActionsCard: activeAction.type === CardTypes.multipleActions,
            result: state.radialSlider.result
        };
    },
    {
        onInteractWithSlider,
        onRadialSliderAnswered
    }
)(memo(Slider, arePropsEqual));
